import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useCreateQrSession, useQrSessionStatus } from '../../queries/qrSessions';
import type { QrSessionRow } from '../../db/qrSessionRepository';

export default function Home() {
  const [qrCode, setQrCode] = useState('');
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(60);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [pcUserEmail, setPcUserEmail] = useState<string | null>(null);
  const [qrHasAuth, setQrHasAuth] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isGeneratingRef = useRef(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const createQrMutation = useCreateQrSession();
  const { data: sessionData } = useQrSessionStatus(isLoggedIn ? null : sessionToken);

  // PC 로그인 상태 감지
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setPcUserEmail(user?.email ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setPcUserEmail(session?.user?.email ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // 모바일 감지
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const generateQRCode = useCallback(async () => {
    // 동시 호출 방지
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    const token = `qr-${Date.now()}-${Math.random().toString(36).substring(7)}`;
    const expiresAt = new Date(Date.now() + 60000).toISOString();

    try {
      // 현재 로그인 세션 가져오기 (로그인 상태면 토큰 포함 → 모바일 자동 로그인)
      const { data: { session } } = await supabase.auth.getSession();
      const authAccessToken = session?.access_token;
      const authRefreshToken = session?.refresh_token;
      setQrHasAuth(!!authAccessToken);

      await createQrMutation.mutateAsync({ token, expiresAt, authAccessToken, authRefreshToken });

      const qrData = `${window.location.origin}/qr-link?token=${token}`;
      const qrSize = isMobile ? 280 : 512;

      const qrCodeUrl = await QRCode.toDataURL(qrData, {
        width: qrSize,
        margin: 2,
        color: {
          dark: '#1a1a1a',
          light: '#FFFFFF',
        },
      });

      setQrCode(qrCodeUrl);
      setSessionToken(token);
      setTimeLeft(60);
    } catch (error) {
      console.error('QR 코드 생성 실패:', error);
    } finally {
      isGeneratingRef.current = false;
    }
  }, [createQrMutation, isMobile]);

  /**
   * @description QR 이미지 클릭 시 링크를 클립보드에 복사한다.
   */
  const handleQRClick = useCallback(async () => {
    if (!sessionToken) return;
    const qrUrl = `${window.location.origin}/qr-link?token=${sessionToken}`;
    try {
      await navigator.clipboard.writeText(qrUrl);
      toast.success('링크가 복사됐어요', { duration: 2000 });
    } catch {
      toast.error('복사에 실패했어요. 직접 선택해서 복사해주세요.');
    }
  }, [sessionToken]);

  // 세션 상태 변경 감지 (useQrSessionStatus 폴링 결과)
  useEffect(() => {
    if (sessionData && sessionData.is_used && sessionData.user_id && !isLoggedIn) {
      setIsLoggedIn(true);

      localStorage.setItem('qr_session_token', sessionData.session_token);
      localStorage.setItem('qr_user_id', sessionData.user_id);
      localStorage.setItem('qr_auto_play', 'true');

      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    }
  }, [sessionData, isLoggedIn, navigate]);

  // Realtime 구독 (폴링의 보완)
  useEffect(() => {
    if (!sessionToken || isLoggedIn) return;

    const channel = supabase
      .channel(`qr-session-${sessionToken}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'qr_sessions',
          filter: `session_token=eq.${sessionToken}`,
        },
        (payload) => {
          const updated = payload.new as QrSessionRow;
          queryClient.setQueryData(["qrSessions", sessionToken], updated);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionToken, isLoggedIn, queryClient]);

  useEffect(() => {
    generateQRCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (timeLeft > 0 && !isLoggedIn) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            generateQRCode();
            return 60;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft, isLoggedIn]);

  // 모바일 화면
  if (isMobile) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
          {/* 로고 */}
          <div className="mb-6">
            <img
              src="https://static.readdy.ai/image/fa9e51599f3a91cf824c0f1e50ffec3c/bbe0145b0a3abb3ea6b08059cd830213.png"
              alt="Qpresen Logo"
              className="h-12 object-contain"
            />
          </div>

          {/* QR 코드 */}
          <div className="flex-shrink-0 mb-6">
            {isLoggedIn ? (
              <div className="w-[280px] h-[280px] flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mb-4 shadow-lg shadow-emerald-200">
                  <i className="ri-check-line text-3xl text-white"></i>
                </div>
                <h2 className="text-lg font-bold text-slate-900 mb-1">{t('home.connection_complete')}</h2>
                <p className="text-sm text-slate-500">{t('home.redirecting_dashboard')}</p>
              </div>
            ) : (
              <>
                {qrCode ? (
                  <img src={qrCode} alt="QR Code" className="w-[280px] h-[280px] cursor-pointer" onClick={handleQRClick} />
                ) : (
                  <div className="w-[280px] h-[280px] flex items-center justify-center">
                    <div className="w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* 로그인 버튼 */}
          <Link
            to="/login"
            className="w-full max-w-[280px] py-3 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors text-center block"
          >
            {t('home.get_started')}
          </Link>
        </div>
      </div>
    );
  }

  // PC 화면
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-stone-50 relative overflow-hidden">
      {/* 배경 장식 요소 */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-emerald-50/60 via-teal-50/40 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-amber-50/50 via-orange-50/30 to-transparent rounded-full blur-3xl translate-y-1/2 -translate-x-1/3"></div>

      {/* 미세한 그리드 패턴 */}
      <div className="absolute inset-0 opacity-[0.015]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, #000 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }}></div>

      {/* 헤더 */}
      <header className="relative z-10 w-full px-8 lg:px-16 py-6 flex items-center justify-end gap-3">
        <Link
          to="/login"
          className="px-6 py-2.5 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors whitespace-nowrap"
        >
          {t('home.get_started')}
        </Link>
      </header>

      {/* 메인 콘텐츠 */}
      <main className="relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-100px)] px-8 lg:px-16">

        {/* 로고 */}
        <div className="mb-8">
          <img
            src="https://static.readdy.ai/image/fa9e51599f3a91cf824c0f1e50ffec3c/bbe0145b0a3abb3ea6b08059cd830213.png"
            alt="Qpresen Logo"
            className="h-28 object-contain"
          />
        </div>

        {/* QR 코드 영역 */}
        <div className="flex-shrink-0">
          {isLoggedIn ? (
            <div className="w-[512px] h-[512px] flex flex-col items-center justify-center">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mb-5 shadow-lg shadow-emerald-200">
                <i className="ri-check-line text-4xl text-white"></i>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">{t('home.connection_complete')}</h2>
              <p className="text-sm text-slate-500">{t('home.redirecting_dashboard')}</p>
            </div>
          ) : (
            <>
              {qrCode ? (
                <img src={qrCode} alt="QR Code" className="w-[512px] h-[512px] cursor-pointer" onClick={handleQRClick} />
              ) : (
                <div className="w-[512px] h-[512px] flex items-center justify-center">
                  <div className="w-10 h-10 border-3 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
              {/* 디버그: auth 상태 */}
              <div className="mt-3 text-xs text-center space-y-1">
                <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${pcUserEmail ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  <span>{pcUserEmail ? `로그인됨: ${pcUserEmail}` : '비로그인'}</span>
                </div>
                {qrCode && (
                  <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ml-2 ${qrHasAuth ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                    <span>{qrHasAuth ? '🔑 QR에 인증 포함' : '⚠️ QR 인증 없음'}</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
